import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import api from '../services/api';

const BulkCanvasProcessor = ({ onProcessComplete }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedScenario, setSelectedScenario] = useState('');
    const [scenarios, setScenarios] = useState([]);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [processingResults, setProcessingResults] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);

    // Load scenarios on component mount
    React.useEffect(() => {
        loadScenarios();
    }, []);

    const loadScenarios = async () => {
        try {
            const response = await api.canvas.getScenarios();
            if (response.data.success) {
                setScenarios(response.data.data);
            }
        } catch (error) {
            console.error('Error loading scenarios:', error);
            toast.error('Failed to load scenarios');
        }
    };

    // File drop handler
    const onDrop = useCallback(async (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;

        if (!selectedScenario) {
            toast.error('Please select a scenario first');
            return;
        }

        setUploadedFile(file);
        setPreviewData(null);
        setProcessingResults(null);

        // Upload and preview file
        const formData = new FormData();
        formData.append('file', file);
        formData.append('scenarioId', selectedScenario);

        try {
            setIsProcessing(true);
            const response = await api.post('/bulk/preview', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                setPreviewData(response.data.data);
                toast.success(`Parsed ${response.data.data.totalRows} rows successfully`);
            } else {
                toast.error(response.data.message || 'Failed to parse file');
            }
        } catch (error) {
            console.error('File upload error:', error);
            toast.error('Failed to upload and parse file');
        } finally {
            setIsProcessing(false);
        }
    }, [selectedScenario]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
            'text/csv': ['.csv']
        },
        maxFiles: 1
    });

    // Process bulk invoices
    const handleBulkProcess = async () => {
        if (!previewData || !previewData.records) {
            toast.error('No data to process');
            return;
        }

        try {
            setIsProcessing(true);
            const response = await api.post('/bulk/process', {
                scenarioId: selectedScenario,
                records: previewData.records.filter(r => r.success),
                options: {
                    batchSize: 10,
                    delayBetweenBatches: 1000
                }
            });

            if (response.data.success) {
                setJobStatus(response.data.data);
                toast.success('Bulk processing started');
                
                // Poll for job status
                pollJobStatus(response.data.data.jobId);
            } else {
                toast.error(response.data.message || 'Failed to start bulk processing');
            }
        } catch (error) {
            console.error('Bulk processing error:', error);
            toast.error('Failed to start bulk processing');
        } finally {
            setIsProcessing(false);
        }
    };

    // Poll job status
    const pollJobStatus = async (jobId) => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await api.get(`/bulk/status/${jobId}`);
                const status = response.data;
                
                setJobStatus(status);
                
                if (status.status === 'completed' || status.status === 'failed') {
                    clearInterval(pollInterval);
                    setProcessingResults(status.result);
                    
                    if (status.status === 'completed') {
                        toast.success('Bulk processing completed');
                        if (onProcessComplete) {
                            onProcessComplete(status.result);
                        }
                    } else {
                        toast.error('Bulk processing failed');
                    }
                }
            } catch (error) {
                console.error('Status polling error:', error);
                clearInterval(pollInterval);
            }
        }, 2000);

        // Clear interval after 5 minutes
        setTimeout(() => clearInterval(pollInterval), 300000);
    };

    // Test canvas formulas with sample data
    const handleTestFormulas = async () => {
        if (!selectedScenario || !previewData?.records?.length) {
            toast.error('No data to test');
            return;
        }

        try {
            setIsProcessing(true);
            const sampleRecords = previewData.records.slice(0, 5); // Test first 5 records
            
            const response = await api.post('/canvas/bulk-evaluate', {
                scenarioId: selectedScenario,
                records: sampleRecords
            });

            if (response.data.success) {
                const results = response.data.data;
                toast.success(`Tested ${results.totalRecords} records. ${results.successfulRecords} successful.`);
                
                // Show results in console for debugging
                console.log('Formula test results:', results);
            } else {
                toast.error('Formula test failed');
            }
        } catch (error) {
            console.error('Formula test error:', error);
            toast.error('Failed to test formulas');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Bulk Invoice Processing with Canvas Formulas
                </h2>

                {/* Scenario Selection */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Scenario
                    </label>
                    <select
                        value={selectedScenario}
                        onChange={(e) => setSelectedScenario(e.target.value)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        disabled={isProcessing}
                    >
                        <option value="">Choose a scenario...</option>
                        {scenarios.map(scenario => (
                            <option key={scenario.scenarioId} value={scenario.scenarioId}>
                                {scenario.scenarioId} - {scenario.description}
                            </option>
                        ))}
                    </select>
                </div>

                {/* File Upload */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload Excel File
                    </label>
                    <div
                        {...getRootProps()}
                        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                            isDragActive 
                                ? 'border-indigo-500 bg-indigo-50' 
                                : 'border-gray-300 hover:border-gray-400'
                        } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <input {...getInputProps()} disabled={isProcessing} />
                        <div className="text-gray-600">
                            {isDragActive ? (
                                <p>Drop the Excel file here...</p>
                            ) : (
                                <div>
                                    <p>Drag & drop an Excel file here, or click to select</p>
                                    <p className="text-sm text-gray-500 mt-2">
                                        Supports .xlsx, .xls, and .csv files
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {uploadedFile && (
                        <div className="mt-2 text-sm text-gray-600">
                            Selected: {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)
                        </div>
                    )}
                </div>

                {/* Preview Data */}
                {previewData && (
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">
                                Data Preview
                            </h3>
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleTestFormulas}
                                    disabled={isProcessing}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                                >
                                    Test Formulas
                                </button>
                                <button
                                    onClick={handleBulkProcess}
                                    disabled={isProcessing || previewData.successfulRows === 0}
                                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                                >
                                    {isProcessing ? 'Processing...' : 'Process All'}
                                </button>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <div className="text-2xl font-bold text-blue-600">
                                        {previewData.totalRows}
                                    </div>
                                    <div className="text-sm text-gray-600">Total Rows</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-green-600">
                                        {previewData.successfulRows}
                                    </div>
                                    <div className="text-sm text-gray-600">Valid Rows</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-red-600">
                                        {previewData.failedRows}
                                    </div>
                                    <div className="text-sm text-gray-600">Failed Rows</div>
                                </div>
                            </div>
                        </div>

                        {/* Sample data table */}
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Row
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            HS Code
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Quantity
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Unit Price
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Sales Tax
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {previewData.records.slice(0, 10).map((record, index) => (
                                        <tr key={index}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.rowIndex}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    record.success 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {record.success ? 'Valid' : 'Error'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.hsCode || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.quantity || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.singleUnitPrice || record.fixedNotifiedValueOrRetailPrice || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.salesTaxApplicable || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Job Status */}
                {jobStatus && (
                    <div className="mb-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Processing Status
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-gray-700">
                                    Job ID: {jobStatus.jobId}
                                </span>
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    jobStatus.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    jobStatus.status === 'failed' ? 'bg-red-100 text-red-800' :
                                    jobStatus.status === 'active' ? 'bg-blue-100 text-blue-800' :
                                    'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {jobStatus.status}
                                </span>
                            </div>
                            
                            {jobStatus.progress !== undefined && (
                                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                    <div 
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${jobStatus.progress}%` }}
                                    ></div>
                                </div>
                            )}
                            
                            <div className="text-sm text-gray-600">
                                Progress: {jobStatus.progress || 0}%
                            </div>
                        </div>
                    </div>
                )}

                {/* Processing Results */}
                {processingResults && (
                    <div className="mb-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Processing Results
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="grid grid-cols-3 gap-4 text-center mb-4">
                                <div>
                                    <div className="text-2xl font-bold text-blue-600">
                                        {processingResults.totalRecords}
                                    </div>
                                    <div className="text-sm text-gray-600">Total Records</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-green-600">
                                        {processingResults.successfulSubmissions}
                                    </div>
                                    <div className="text-sm text-gray-600">Successful</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-red-600">
                                        {processingResults.failedSubmissions}
                                    </div>
                                    <div className="text-sm text-gray-600">Failed</div>
                                </div>
                            </div>
                            
                            {processingResults.errors && processingResults.errors.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="font-medium text-red-800 mb-2">Errors:</h4>
                                    <div className="max-h-40 overflow-y-auto">
                                        {processingResults.errors.map((error, index) => (
                                            <div key={index} className="text-sm text-red-600 mb-1">
                                                Row {error.rowIndex}: {error.error}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Instructions */}
                <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-medium text-blue-900 mb-2">Instructions:</h3>
                    <ol className="text-sm text-blue-800 space-y-1">
                        <li>1. Select a scenario that matches your invoice type</li>
                        <li>2. Upload an Excel file with invoice data</li>
                        <li>3. Review the preview and test formulas if needed</li>
                        <li>4. Click "Process All" to submit invoices to FBR</li>
                        <li>5. Monitor the processing status and results</li>
                    </ol>
                </div>
            </div>
        </div>
    );
};

export default BulkCanvasProcessor;